/**
 * FeedShareModal — Facebook-style "Create post" composer.
 *
 * A text-primary post box: a large textarea up top, then an "Add to your post"
 * toolbar that can attach ONE of:
 *   • Link  — paste URL, auto-preview via OG tags
 *   • Video — paste YouTube URL, oEmbed preview
 *   • Content — search published chronicles / how-tos / articles
 *   • Photo — DISABLED ("Soon") until the media-moderation pipeline ships
 *             (CSAM + EXIF strip + NCMEC; see outputs/EPIC_Media_Phase.md).
 *
 * Posting modes:
 *   • Text only (no attachment) → a 'text' status post.
 *   • Text + attachment        → the attachment share, with the text as comment.
 *
 * `allowText={false}` (used by the Project Journal) keeps the old behavior: the
 * textarea is an optional comment and an attachment is required to post — the
 * Journal's submit handler only understands link/video/chronicle.
 *
 * Uses /api/News/preview for live URL previews and /api/News/share to submit.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import CloseButton from '../CloseButton/CloseButton';
import { previewUrl, shareToCommunityFeed, type UrlPreview, type ShareType } from '../../api/feedShare';
import { apiFetch } from '../../api/client';
import { mediaSrc } from '../../api/mediaUrl';
import { resolveChronicleType } from '../../Data/Chronicles/chronicleTypes';
import styles from './FeedShareModal.module.css';
import { sanitizeEmbed } from '../../utils/sanitizeHtml';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** What's attached to the post, if anything. */
type Attach = 'none' | 'link' | 'video' | 'chronicle';

/** Payload handed to a custom submit target (e.g. the Project Journal) so the
 *  same flexible share UI can persist somewhere other than the community feed.
 *  Includes the already-fetched OG/oEmbed preview so the target needn't re-scrape. */
export interface ShareSubmitPayload {
  shareType: ShareType;
  url?: string;
  chronicleGuid?: string;
  comment?: string;
  preview?: UrlPreview | null;
  chronicle?: { chronicleGuid: string; title: string; heroImageUrl?: string; categoryKey: string } | null;
}

interface Props {
  /** Required only for the default community-feed submit path. */
  communityGuid?: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful share. `firstPost` = true exactly once,
   *  on the user's first-ever feed share (DEV-1441 Voice moment). */
  onShared: (firstPost?: boolean) => void;
  /** Optional header title override (defaults to the community-feed title). */
  headerTitle?: string;
  /** Optional label for the content tab (defaults to "Chronicle"). The Project
   *  Journal passes "My Content" — the search already spans all chronicle types
   *  (How-To, Topic Tracker, Scholarly, News, …) since they are one entity. */
  contentTabLabel?: string;
  /** When provided, replaces the default `shareToCommunityFeed` submit — the
   *  modal hands back the chosen share + its preview and the caller persists it.
   *  Used by the Project Journal to create a journal post instead. */
  onSubmitShare?: (payload: ShareSubmitPayload) => Promise<void>;
  /** Which attachment to open with. The main-feed composer uses this so its
   *  Video / Link / Content buttons land pre-attached. Omit for a blank text post. */
  initialShareType?: ShareType;
  /** Whether text-only posts are allowed (default true). The Project Journal
   *  passes false: its handler only persists link/video/chronicle, so the
   *  textarea acts as an optional comment and an attachment is required. */
  allowText?: boolean;
}

interface ChronicleOption {
  chronicleGuid: string;
  title: string;
  categoryKey: string;
  heroImageUrl?: string;
  missionNarrative?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const FeedShareModal: React.FC<Props> = ({
  communityGuid, isOpen, onClose, onShared, headerTitle, contentTabLabel,
  onSubmitShare, initialShareType, allowText = true,
}) => {
  const { t } = useAppTranslation('pages');
  const { user } = useAuth();

  const firstName = (user?.displayName ?? '').trim().split(/\s+/)[0] || '';

  /** Human label for a chronicle's category (icon + localized type name). */
  const typeLabel = useCallback((categoryKey: string): string => {
    const td = resolveChronicleType(categoryKey);
    return td ? `${td.icon} ${t(td.labelKey)}` : categoryKey;
  }, [t]);

  // Default attachment: text mode when allowed, else a link (Journal needs one).
  const defaultAttach: Attach = (initialShareType && initialShareType !== 'text')
    ? initialShareType
    : (allowText ? 'none' : 'link');

  const [attach, setAttach] = useState<Attach>(defaultAttach);
  const [url, setUrl] = useState('');
  const [comment, setComment] = useState('');
  const [preview, setPreview] = useState<UrlPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chronicle search
  const [chronicleSearch, setChronicleSearch] = useState('');
  const [chronicles, setChronicles] = useState<ChronicleOption[]>([]);
  const [selectedChronicle, setSelectedChronicle] = useState<ChronicleOption | null>(null);
  const [chroniclesLoading, setChroniclesLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // ── Reset on open ─────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setAttach(defaultAttach);
      setUrl('');
      setComment('');
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(false);
      setSubmitting(false);
      setSuccess(false);
      setError(null);
      setChronicleSearch('');
      setChronicles([]);
      setSelectedChronicle(null);
    }
  }, [isOpen, defaultAttach]);

  // ── URL Preview debounce ──────────────────────────────────
  useEffect(() => {
    if (attach !== 'link' && attach !== 'video') return;
    if (!url.trim() || url.trim().length < 10) {
      setPreview(null);
      setPreviewError(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(false);
      try {
        const p = await previewUrl(url.trim());
        setPreview(p);
        setPreviewError(!p.title);
      } catch {
        setPreviewError(true);
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(debounceRef.current);
  }, [url, attach]);

  // ── Chronicle search ──────────────────────────────────────
  useEffect(() => {
    if (attach !== 'chronicle') return;
    if (!chronicleSearch.trim()) {
      setChronicles([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setChroniclesLoading(true);
      try {
        // ListChronicles returns { items: [...], total, page, pageSize }
        const resp = await apiFetch<{ items: any[] }>(
          `/api/Chronicles?status=published&search=${encodeURIComponent(chronicleSearch.trim())}&pageSize=10`
        );
        const mapped: ChronicleOption[] = (resp.items || []).map((c: any) => ({
          chronicleGuid: c.chronicleGuid,
          title: c.title,
          categoryKey: c.categoryKey || '',
          heroImageUrl: c.heroImageUrl || undefined,
          missionNarrative: c.subtitle || undefined,
        }));
        setChronicles(mapped);
      } catch {
        setChronicles([]);
      } finally {
        setChroniclesLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [chronicleSearch, attach]);

  // ── Toolbar: toggle which attachment is active ────────────
  const chooseAttach = useCallback((next: Attach) => {
    setAttach((cur) => {
      const toggled = cur === next ? (allowText ? 'none' : cur) : next;
      // Clear the other attachment's transient state when switching.
      setPreview(null);
      setPreviewError(false);
      setUrl('');
      setSelectedChronicle(null);
      setChronicleSearch('');
      return toggled;
    });
  }, [allowText]);

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    // DEV-1441 — true when the share response says this was the user's
    // first-ever feed post; threaded to onShared for the Voice ribbon.
    let firstPost = false;
    try {
      const text = comment.trim();
      if (onSubmitShare) {
        // Custom target (e.g. the Project Journal).
        if (attach === 'chronicle') {
          if (!selectedChronicle) return;
          await onSubmitShare({
            shareType: 'chronicle',
            chronicleGuid: selectedChronicle.chronicleGuid,
            comment: text || undefined,
            chronicle: {
              chronicleGuid: selectedChronicle.chronicleGuid,
              title: selectedChronicle.title,
              heroImageUrl: selectedChronicle.heroImageUrl,
              categoryKey: selectedChronicle.categoryKey,
            },
          });
        } else if (attach === 'link' || attach === 'video') {
          if (!url.trim()) return;
          await onSubmitShare({ shareType: attach, url: url.trim(), comment: text || undefined, preview });
        } else {
          // Text-only via a custom target (not used today; Journal sets allowText=false).
          if (!text) return;
          await onSubmitShare({ shareType: 'text', comment: text });
        }
      } else if (attach === 'chronicle') {
        if (!selectedChronicle) return;
        const res = await shareToCommunityFeed({
          scopeGuid: communityGuid!,
          shareType: 'chronicle',
          chronicleGuid: selectedChronicle.chronicleGuid,
          comment: text || undefined,
        });
        firstPost = !!res.firstPost;
      } else if (attach === 'link' || attach === 'video') {
        if (!url.trim()) return;
        const res = await shareToCommunityFeed({ scopeGuid: communityGuid!, shareType: attach, url: url.trim(), comment: text || undefined });
        firstPost = !!res.firstPost;
      } else {
        // Text-only status post.
        if (!text) return;
        const res = await shareToCommunityFeed({ scopeGuid: communityGuid!, shareType: 'text', comment: text });
        firstPost = !!res.firstPost;
      }
      setSuccess(true);
      setTimeout(() => {
        onShared(firstPost);
        onClose();
      }, 1000);
    } catch (err: any) {
      const msg = err?.message || err?.body?.error || 'Share failed — check that the backend is running.';
      console.error('Share failed:', err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [attach, url, comment, selectedChronicle, communityGuid, onShared, onClose, onSubmitShare, preview]);

  const hasAttachment =
    (attach === 'chronicle' && !!selectedChronicle) ||
    ((attach === 'link' || attach === 'video') && url.trim().length > 5);

  const canSubmit =
    !submitting && !success &&
    (hasAttachment || (allowText && attach === 'none' && comment.trim().length > 0));

  if (!isOpen) return null;

  // DEV-1453: single source of truth for the composer prompt. The inline
  // FeedComposer and this modal are one flow — they must not greet the user
  // with two different lines. Both read feed.composer.prompt.
  const textPlaceholder = attach === 'none' && allowText
    ? (firstName
        ? t('feed.composer.prompt', { name: firstName })
        : t('feed.composer.promptNoName'))
    : t('communityDetail.news.shareComment');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{headerTitle ?? t('communityDetail.news.shareModalTitle')}</h2>
          <CloseButton onClick={onClose} size="md" />
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Author row */}
          <div className={styles.authorRow}>
            {user?.profileImageUrl ? (
              <img className={styles.authorAvatar} src={mediaSrc(user.profileImageUrl) ?? undefined} alt="" />
            ) : (
              <div className={styles.authorAvatarFallback} aria-hidden>
                {firstName.charAt(0).toUpperCase() || '·'}
              </div>
            )}
            <div className={styles.authorMeta}>
              <div className={styles.authorName}>{user?.displayName || t('communityDetail.news.shareYou')}</div>
              <div className={styles.audiencePill}>{t('communityDetail.news.shareAudience')}</div>
            </div>
          </div>

          {/* Text body (primary) */}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={textPlaceholder}
            className={styles.postText}
            rows={4}
            autoFocus
          />

          {/* Attachment input: link / video */}
          {(attach === 'link' || attach === 'video') && (
            <div className={styles.field}>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t('communityDetail.news.sharePlaceholderUrl')}
                className={styles.input}
              />
            </div>
          )}

          {/* Attachment input: chronicle search */}
          {attach === 'chronicle' && !selectedChronicle && (
            <div className={styles.field}>
              <input
                type="text"
                value={chronicleSearch}
                onChange={(e) => setChronicleSearch(e.target.value)}
                placeholder={t('communityDetail.news.shareChronicleSearch')}
                className={styles.input}
              />
              {chroniclesLoading && (
                <div className={styles.previewLoading}>{t('communityDetail.news.sharePreviewLoading')}</div>
              )}
              {chronicles.length > 0 && (
                <div className={styles.chronicleList}>
                  {chronicles.map((c) => (
                    <button
                      key={c.chronicleGuid}
                      className={styles.chronicleOption}
                      onClick={() => setSelectedChronicle(c)}
                    >
                      {c.heroImageUrl && (
                        <img src={mediaSrc(c.heroImageUrl) ?? undefined} alt="" className={styles.chronicleThumb} />
                      )}
                      <div className={styles.chronicleInfo}>
                        <div className={styles.chronicleName}>{c.title}</div>
                        <div className={styles.chronicleExcerpt}>{typeLabel(c.categoryKey)}</div>
                        {c.missionNarrative && (
                          <div className={styles.chronicleExcerpt}>
                            {c.missionNarrative.slice(0, 120)}{c.missionNarrative.length > 120 ? '…' : ''}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {!chroniclesLoading && chronicleSearch.length > 2 && chronicles.length === 0 && (
                <div className={styles.empty}>{t('communityDetail.news.shareChronicleNone')}</div>
              )}
            </div>
          )}

          {/* Selected chronicle preview */}
          {attach === 'chronicle' && selectedChronicle && (
            <div className={styles.previewCard}>
              {selectedChronicle.heroImageUrl && (
                <img src={mediaSrc(selectedChronicle.heroImageUrl) ?? undefined} alt="" className={styles.previewImage} />
              )}
              <div className={styles.previewBody}>
                <div className={styles.previewTitle}>{selectedChronicle.title}</div>
                <div className={styles.previewSource}>{typeLabel(selectedChronicle.categoryKey)}</div>
              </div>
              <button className={styles.clearBtn} onClick={() => setSelectedChronicle(null)}>×</button>
            </div>
          )}

          {/* URL preview */}
          {(attach === 'link' || attach === 'video') && previewLoading && (
            <div className={styles.previewLoading}>{t('communityDetail.news.sharePreviewLoading')}</div>
          )}
          {(attach === 'link' || attach === 'video') && previewError && !previewLoading && url.trim().length > 10 && (
            <div className={styles.previewError}>{t('communityDetail.news.sharePreviewError')}</div>
          )}
          {(attach === 'link' || attach === 'video') && preview && !previewLoading && (
            <div className={styles.previewCard}>
              {preview.embedHtml && preview.embedType === 'video' ? (
                <div
                  className={styles.embedContainer}
                  dangerouslySetInnerHTML={{ __html: sanitizeEmbed(preview.embedHtml) }}
                />
              ) : preview.imageUrl ? (
                <img src={mediaSrc(preview.imageUrl) ?? undefined} alt="" className={styles.previewImage} />
              ) : null}
              <div className={styles.previewBody}>
                <div className={styles.previewTitle}>{preview.title || url}</div>
                {preview.description && (
                  <div className={styles.previewDesc}>{preview.description.slice(0, 200)}</div>
                )}
                {preview.siteName && (
                  <div className={styles.previewSource}>{preview.siteName}</div>
                )}
              </div>
            </div>
          )}

          {/* Add-to-your-post toolbar */}
          <div className={styles.addBar}>
            <span className={styles.addBarLabel}>{t('communityDetail.news.addToPost')}</span>
            <div className={styles.addBarActions}>
              <button
                type="button"
                className={`${styles.addBtn} ${attach === 'video' ? styles.addBtnActive : ''}`}
                onClick={() => chooseAttach('video')}
                title={t('communityDetail.news.shareVideo')}
              >
                🎥
              </button>
              <button
                type="button"
                className={`${styles.addBtn} ${attach === 'link' ? styles.addBtnActive : ''}`}
                onClick={() => chooseAttach('link')}
                title={t('communityDetail.news.shareLink')}
              >
                🔗
              </button>
              <button
                type="button"
                className={`${styles.addBtn} ${attach === 'chronicle' ? styles.addBtnActive : ''}`}
                onClick={() => chooseAttach('chronicle')}
                title={contentTabLabel ?? t('communityDetail.news.shareChronicle')}
              >
                📄
              </button>
              {/* Photo disabled until the media-moderation pipeline ships. */}
              <button
                type="button"
                className={`${styles.addBtn} ${styles.addBtnDisabled}`}
                disabled
                title={t('communityDetail.news.sharePhotoSoon')}
              >
                📷
                <span className={styles.soonBadge}>{t('communityDetail.news.soon')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {error && <div className={styles.errorMsg}>{error}</div>}
          {success ? (
            <div className={styles.successMsg}>{t('communityDetail.news.shareSuccess')}</div>
          ) : (
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? t('communityDetail.news.shareSubmitting') : t('communityDetail.news.postBtn')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedShareModal;
